import { useContext, useEffect, useMemo, useState } from "react";
import { useBoolean } from "@fluentui/react-hooks"
import { FontIcon, Icon, mergeStyles, Stack, Text, TooltipHost } from "@fluentui/react";

import { AskResponse, ChatMessage, Citation, Conversation } from "../../../../ApplicationCode/Common/Chatbot/api";
import { parseAnswer } from "./AnswerParser";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import supersub from 'remark-supersub';
import { HttpHelper } from "../../../../Core/Http/HttpHelper";
import { t } from "i18next";
import { AppStateContext } from "../../../../Core/State/ChatbotAppProvider";

interface Props {
    answer: AskResponse;
    onCitationClicked: (citedDocument: Citation) => void;
    onFollowUpQuestionClicked?: (question: string) => void;
    onOpenSupportRequestClick: () => void;
    isLastIteraction: boolean;
    showFeedbackIcons?: boolean;
    feedbackMessage?: ChatMessage;
    followupQuestions?: String[];
}


export const Answer = ({
    answer,
    onCitationClicked,
    onOpenSupportRequestClick,
    onFollowUpQuestionClicked,
    isLastIteraction,
    showFeedbackIcons,
    feedbackMessage,
    followupQuestions
}: Props) => {
    const appStateContext = useContext(AppStateContext);
    const [isRefAccordionOpen, { toggle: toggleIsRefAccordionOpen }] = useBoolean(false);
    const [isRefFQAccordionOpen, { toggle: toggleIsRefFQAccordionOpen }] = useBoolean(true);
    const [isLikeOver, setIsLikeOver] = useState<boolean>(false);
    const [isDisLikeOver, setIsDisLikeOver] = useState<boolean>(false);
    const [isPositiveFeedbackClicked, setIsPositiveFeedbackClicked] = useState<boolean>(false);
    const [isNegativeFeedbackClicked, setIsNegativeFeedbackClicked] = useState<boolean>(false);

    const embedded: boolean = (HttpHelper.getParameter("embedded") === "true");

    const parsedAnswer = useMemo(() => parseAnswer(answer), [answer]);
    const [chevronIsExpanded, setChevronIsExpanded] = useState(isRefAccordionOpen);
    const [chevronFQIsExpanded, setChevronFQIsExpanded] = useState(isRefFQAccordionOpen);

    //#region Styles

    const answerContainerStyles = mergeStyles([{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        background: "#FFFFFF",
        boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.14), 0px 0px 2px rgba(0, 0, 0, 0.12)",
        borderRadius: "5.419px"
    }]);

    const answerTextStyles = mergeStyles([{
        fontStyle: "normal",
        fontWeight: "400",
        fontSize: "clamp(11px, 0.7dvw, 18px)",
        lineHeight: "20px",
        color: "#323130",
        flex: "none",
        order: "1",
        alignSelf: "stretch",
        flexGrow: "0",
        margin: "5px",
        whiteSpace: "normal",
        wordWrap: "break-word",
        maxWidth: "800px",
        overflowX: "auto",
        overflowY: "hidden",
        fontFamily: "Nunito"
    }]);

    const answerFooterStyles = mergeStyles([{
        display: "flex",
        flexFlow: "row nowrap",
        width: "100%",
        height: "auto",
        boxSizing: "border-box",
        justifyContent: "space-between"
    }]);

    const accordionTitleStyles = mergeStyles([{
        marginRight: "5px",
        marginLeft: "10px",
        fontStyle: "normal",
        fontWeight: "400",
        fontSize: ".800rem",
        lineHeight: "16px",
        display: "flex",
        alignItems: "center",
        color: "#616161",
        fontFamily: "Nunito",
        selectors: {
            ':hover': {
                cursor: "pointer"
            }
        }
    }]);

    const accordionIconStyles = mergeStyles([{
        display: "inline-flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        padding: "0px",
        marginTop: "2px",
        color: "#616161",
        fontSize: ".800rem",
        selectors: {
            ':hover': {
                cursor: "pointer"
            }
        }

    }]);

    const citationContainerStyles = mergeStyles([{
        marginLeft: "10px",
        fontStyle: "normal",
        fontWeight: "600",
        lineHeight: "16px",
    
        color: "#2A7DE1",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        padding: "4px 6px",
        gap: "4px",
        border: "1px solid #D1D1D1",
        borderRadius: "4px",
        fontSize: ".875rem",
        "&:hover": {    
            textDecoration: "underline",
            cursor: "pointer"
        }
    }]);

    const answerDisclaimerContainerStyles = mergeStyles([{
        padding: "8px",
        display: "flex"
    }]);

    const answerDisclaimerStyles = mergeStyles([{
        fontStyle: "normal",
        fontWeight: "400",
        fontSize: ".700rem",
    
        display: "flex",
        alignItems: "center",
        textAlign: "left",
        color: "#707070",
        fontFamily: "Nunito"
    }]);

    const answerFeedbackStyles = mergeStyles([{
        fontStyle: "normal",
        fontWeight: "400",
        fontSize: ".875rem",
        lineHeight: "16px",
    
        display: "flex",
        alignItems: "center",
        textAlign: "center",
        color: "#707070",
        gap: "10px"
    }]);

    const answerFeedbackIconStyles = mergeStyles([{
        cursor: "pointer",
        fontSize: "1rem",
        gap: "4px",
    }]);

    //#endregion


    const handleChevronClick = () => {
        setChevronIsExpanded(!chevronIsExpanded);
        toggleIsRefAccordionOpen();
    };

    const handleFQChevronClick = () => {
    setChevronFQIsExpanded(!chevronFQIsExpanded);
    toggleIsRefFQAccordionOpen();
    };

    useEffect(() => {
        setChevronIsExpanded(isRefAccordionOpen);
    }, [isRefAccordionOpen]);

    const createCitationFilepath = (citation: Citation, index: number, truncate: boolean = false) => {
        return citation.title!;
    }

    const handleFeedbackClick = (isPositive: boolean) => {
        let conversation: Conversation | null | undefined;
        if((appStateContext?.state?.currentChat !== null) && (appStateContext?.state?.currentChat !== undefined))
            conversation = appStateContext?.state?.currentChat;
        
        let currentMessage = feedbackMessage;
        currentMessage!.positiveFeedback = isPositive;
        currentMessage!.negativeFeedback! = !isPositive;
                                        
        appStateContext?.dispatch({ type: 'UPDATE_FEEDBACK', convId: conversation?.id!, message: currentMessage!});

        if(isPositive){
            setIsPositiveFeedbackClicked(true);
            setIsNegativeFeedbackClicked(false);
        }
        else{
            setIsPositiveFeedbackClicked(false);
            setIsNegativeFeedbackClicked(true);
        }
    }

    return (
        <>
            <Stack 
                className={answerContainerStyles} 
                tabIndex={0}
            >
                <Stack.Item>
                    <ReactMarkdown
                        linkTarget="_blank" 
                        remarkPlugins={[remarkGfm, supersub]}
                        children={parsedAnswer.markdownFormatText}
                        className={answerTextStyles}
                    />
                </Stack.Item>
                <Stack 
                    verticalFill 
                    verticalAlign="space-between" 
                    wrap 
                    className={answerFooterStyles} 
                    tokens={{
                        childrenGap: 5
                    }}
                >
                    {
                        followupQuestions != undefined && followupQuestions.length > 0 ?
                        (
                            <Stack.Item
                                onKeyDown={e => e.key === "Enter" || e.key === " " ? toggleIsRefFQAccordionOpen() : null}
                            >
                                <Stack style={{width: "100%"}} >
                                    <Stack 
                                        horizontal 
                                        horizontalAlign='start' 
                                        verticalAlign='center'
                                    >
                                        <Text
                                            className={accordionTitleStyles}
                                            onClick={handleFQChevronClick}
                                            aria-label="Open references"
                                            tabIndex={0}
                                            role="button"
                                        >
                                        <span>
                                            {
                                                followupQuestions.length + t("chatbot:answer:followupQuestions")
                                            }
                                        </span>
                                        </Text>
                                        <FontIcon 
                                            className={accordionIconStyles}
                                            onClick={handleFQChevronClick} 
                                            iconName={chevronFQIsExpanded ? 'ChevronDown' : 'ChevronRight'}
                                        />
                                    </Stack>
                                </Stack>
                            </Stack.Item>
                        )
                        :
                        <>
                        </>
                    }
                    {
                        chevronFQIsExpanded && followupQuestions != undefined &&
                            <div 
                                style={{ 
                                    marginTop: 8, 
                                    paddingBottom: 10, 
                                    display: "flex", 
                                    flexFlow: "row wrap", 
                                    maxHeight: "150px", 
                                    overflow:"auto", 
                                    gap: "4px" 
                                }}
                            >
                                {
                                    followupQuestions.map((followupQuestion, idx) => {
                                        return (
                                            <span 
                                                tabIndex={0} 
                                                role="link" 
                                                key={idx} 
                                                onClick={() => {
                                                    onFollowUpQuestionClicked!(followupQuestion.toString());
                                                }} 
                                                style={{
                                                    marginLeft: 10,
                                                    fontStyle: "normal",
                                                    fontWeight: 600,
                                                    fontSize: ".875rem",
                                                    color: "#115EA3",
                                                    cursor: "pointer"    ,
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    alignItems: "center",
                                                    gap: 4
                                                }}
                                            >
                                                {followupQuestion}
                                            </span>
                                        );
                                    })
                                }
                            </div>
                    }
                    {
                        (!embedded) && !!parsedAnswer.citations.filter(e => e.title).length &&
                            <Stack.Item
                                onKeyDown={e => e.key === "Enter" || e.key === " " ? toggleIsRefAccordionOpen() : null}
                            >
                                <Stack style={{width: "100%"}} >
                                    <Stack 
                                        horizontal 
                                        horizontalAlign='start' 
                                        verticalAlign='center'
                                    >
                                        <Text
                                            className={accordionTitleStyles}
                                            onClick={toggleIsRefAccordionOpen}
                                            aria-label="Open references"
                                            tabIndex={0}
                                            role="button"
                                        >
                                            <span>
                                                {
                                                    parsedAnswer.citations.filter(e => e.title).length > 1 ? parsedAnswer.citations.filter(e => e.title).length + t("chatbot:answer:citationCount") : t("chatbot:answer:citationOne")
                                                }
                                            </span>
                                        </Text>
                                        <FontIcon 
                                            className={accordionIconStyles}
                                            onClick={handleChevronClick} iconName={chevronIsExpanded ? 'ChevronDown' : 'ChevronRight'}
                                        />
                                    </Stack>
                                    
                                </Stack>
                            </Stack.Item>
                    }
                    {
                        chevronIsExpanded && 
                            <div 
                                style={{ 
                                    marginTop: 8, 
                                    paddingBottom: 10, 
                                    display: "flex", 
                                    flexFlow: "row wrap", 
                                    maxHeight: "150px", 
                                    overflow:"auto", 
                                    gap: "4px" 
                                }}
                            >
                                {
                                    parsedAnswer.citations.map((citation, idx) => {
                                        if (citation.title)
                                            return (
                                                <span 
                                                    title={createCitationFilepath(citation, ++idx)} 
                                                    tabIndex={0} 
                                                    role="link" 
                                                    key={idx} 
                                                    onClick={() => {
                                                        if (citation.url)
                                                            window.open(citation.url)
                                                    }} 
                                                    onKeyDown={e => e.key === "Enter" || e.key === " " ? onCitationClicked(citation) : null}
                                                    className={citationContainerStyles}
                                                    aria-label={createCitationFilepath(citation, idx)}
                                                >
                                                    {citation.title!}
                                                </span>
                                            );
                                            
                                        return <></>;
                                    })
                                }
                            </div>
                    }
                    <Stack.Item 
                        className={answerDisclaimerContainerStyles}
                    >
                        <div style={{
                            display: 'flex',
                            flexFlow: 'wrap',
                            gap: '10px'
                        }}>
                            <span 
                                className={answerDisclaimerStyles}
                            >
                                {t("chatbot:answer:aiDisclamer")!}
                            </span>
                            {
                                showFeedbackIcons ?
                                    <span className={answerFeedbackStyles}>
                                        {
                                            isLikeOver || isPositiveFeedbackClicked ?
                                            <TooltipHost content={t("chatbot:answer:like")!}>
                                                <Icon iconName="LikeSolid" onMouseLeave={() => setIsLikeOver(false)} className={answerFeedbackIconStyles} onClick={() => {
                                                    handleFeedbackClick(true);
                                                }} />    
                                            </TooltipHost>
                                            :
                                            <TooltipHost content={t("chatbot:answer:like")!}>
                                                <Icon iconName="Like" onMouseEnter={() => setIsLikeOver(true)} className={answerFeedbackIconStyles} />
                                            </TooltipHost>
                                        }
                                        {
                                            isDisLikeOver || isNegativeFeedbackClicked ?
                                            <TooltipHost content={t("chatbot:answer:dislike")!}>
                                                <Icon iconName="DislikeSolid" onMouseLeave={() => setIsDisLikeOver(false)} className={answerFeedbackIconStyles} onClick={() => {
                                                    handleFeedbackClick(false);
                                                }} />    
                                            </TooltipHost>
                                            :
                                            <TooltipHost content={t("chatbot:answer:dislike")!}>
                                                <Icon iconName="Dislike" onMouseEnter={() => setIsDisLikeOver(true)} className={answerFeedbackIconStyles} />
                                            </TooltipHost>
                                        }
                                    </span>
                                    :
                                    <></>
                            }
                        </div>
                    </Stack.Item>
                </Stack>
            </Stack>
        </>
    );
};

interface WelcomeMessageProps {
    isSupportInProgress: boolean;
    onLinkClick: () => void;
}

export const WelcomeMessage: React.FC<WelcomeMessageProps> = (prop) => {

    const answerContainerStyles = mergeStyles([{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        padding: "8.1285px",
        gap: "5.42px",
        background: "#FFFFFF",
        boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.14), 0px 0px 2px rgba(0, 0, 0, 0.12)",
        borderRadius: "5.419px"
    }]);

    const answerTextStyles = mergeStyles([{
        fontStyle: "normal",
        fontWeight: "400",
        fontSize: "clamp(11px, 0.7dvw, 18px)",
        lineHeight: "20px",
        color: "#323130",
        flex: "none",
        order: "1",
        alignSelf: "stretch",
        flexGrow: "0",
        margin: "11px",
        whiteSpace: "normal",
        wordWrap: "break-word",
        maxWidth: "800px",
        overflowX: "auto",
        overflowY: "hidden",
        fontFamily: "Nunito"
    }]);

    return (
        <>
            <Stack className={answerContainerStyles} tabIndex={0}>
                <Stack.Item>
                    <ReactMarkdown
                        components={{
                            a: ({node, ...props}) => <a {...props} href="javascript:void(0);" style={{color:"#000",pointerEvents:prop.isSupportInProgress ? "none" : "auto"}} onMouseOver={(element)=>{element.currentTarget.style.color="gray"}} onMouseLeave={(element)=>{element.currentTarget.style.color="#000"}} onClick={()=>{prop.onLinkClick()}}/>
                        }}
                        remarkPlugins={[remarkGfm, supersub]}
                        children={t("chatbot:chat:welcomeMessage")!}
                        className={answerTextStyles}
                    />
                </Stack.Item>
            </Stack>
        </>
    );
};
